import { useCallback, useEffect, useMemo, useState, type SyntheticEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, StatusBadge } from '@kss/ui';
import { useAuth } from '@kss/auth';
import {
  enumLabel,
  invoiceService,
  type Invoice,
} from '@kss/api';
import {
  ChevronRight,
  Download,
  Receipt,
  RefreshCw,
} from 'lucide-react';

/**
 * Customer-facing invoice history.
 *
 * Pulls invoices from `invoiceService.filter({ customerId: user.id })` —
 * `user.id` is the JWT-authoritative customer identifier that the
 * checkout flow already uses when creating orders and invoices (see
 * `apps/web/src/shop/CheckoutPage.tsx`). Each row deep-links to the
 * `/account/invoices/:id` detail route, which renders the same
 * print-ready layout the panel's `/panel/invoices/:id/preview`
 * uses. Bug ref: SSO ISSUE 4 — "customer-facing invoices section
 * displays invoices incorrectly".
 */
export default function AccountInvoices() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await invoiceService.filter({ customerId: user.id });
      setInvoices(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setError((e as Error)?.message ?? 'Failed to load invoices');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const sorted = useMemo(
    () =>
      [...invoices].sort((a, b) => {
        const dateA = a.invoiceDate ?? '';
        const dateB = b.invoiceDate ?? '';
        return dateB > dateA ? 1 : -1;
      }),
    [invoices]
  );

  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">
          My invoices
        </h1>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && sorted.length === 0 && !error && (
        <Card className="grid place-items-center gap-2 p-10 text-center">
          <Receipt className="h-8 w-8 text-[var(--color-text-muted)]" />
          <p className="text-sm font-medium text-[var(--color-text-primary)]">
            No invoices yet
          </p>
          <p className="max-w-sm text-xs text-[var(--color-text-secondary)]">
            Invoices for your paid or shipped orders appear here — click any
            row to open the print-ready copy.
          </p>
        </Card>
      )}

      {sorted.map((inv) => {
        const open = () =>
          inv.invoiceId && navigate(`/account/invoices/${inv.invoiceId}`);
        const download = (e: SyntheticEvent) => {
          /* Stop the row's own click handler from also firing (it
             would otherwise navigate WITHOUT the `?action=download`
             query param and no auto-export would trigger). */
          e.stopPropagation();
          if (inv.invoiceId) {
            navigate(`/account/invoices/${inv.invoiceId}?action=download`);
          }
        };
        return (
          <Card key={inv.invoiceId ?? inv.invoiceNumber} className="p-0">
            {/* Row itself is a div (not a button) so we can nest a
                real Download button inside it — nested <button>
                elements are invalid HTML. Keyboard + a11y is
                preserved via role="button" + Enter/Space handling. */}
            <div
              role="button"
              tabIndex={0}
              onClick={open}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  open();
                }
              }}
              className="grid w-full grid-cols-[1fr_auto] items-center gap-3 px-5 py-4 text-left hover:bg-[var(--color-bg-card-hover)] cursor-pointer"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-[var(--color-text-primary)]">
                  {inv.invoiceNumber ?? inv.invoiceId ?? '—'}
                </p>
                <p className="text-xs text-[var(--color-text-secondary)]">
                  Issued {(inv.invoiceDate ?? '').slice(0, 10) || '—'}
                  {inv.dueDate && (
                    <>
                      {' · Due '}
                      {inv.dueDate.slice(0, 10)}
                    </>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge
                  status={enumLabel(
                    'InvoiceStatus',
                    typeof inv.status === 'number' ? inv.status : Number(inv.status)
                  )}
                />
                <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                  ₹ {Number(inv.totalAmount ?? 0).toLocaleString('en-IN')}
                </p>
                {/* Download button — navigates to the detail page with
                    `?action=download` so the shared `InvoiceDocument`
                    renders and its `useExport().exportToPdf` fires
                    automatically. Same canonical PDF the panel
                    produces. Bug ref: SSO ROUND-2 ISSUE 4. */}
                <button
                  type="button"
                  onClick={download}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation();
                    }
                  }}
                  disabled={!inv.invoiceId}
                  aria-label={`Download invoice ${inv.invoiceNumber ?? ''} PDF`}
                  title="Download PDF"
                  className="rounded p-1 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-card-hover)] hover:text-[var(--color-text-primary)] disabled:opacity-40"
                >
                  <Download className="h-4 w-4" aria-hidden="true" />
                </button>
                <ChevronRight
                  className="h-4 w-4 text-[var(--color-text-muted)]"
                  aria-hidden="true"
                />
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
